/**
 * Charts Lab — an evaluation surface for AG Charts (ag-charts-community),
 * plotting live KPI data from Snowflake so we can judge whether it's worth
 * adopting alongside / instead of recharts. Every chart is rendered twice from
 * the same data + brand palette: once with AG Charts, once with recharts.
 *
 * Scope: frontend-only, reuses the existing /api/kpi/dashboard endpoint — no new
 * backend, no new Snowflake query. Gated on kpi.read (the data it consumes).
 */
import { useMemo, useState } from "react";

import { BarChart3, ExternalLink, RefreshCw } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useKpiDashboard, useKpiRefresh } from "@/features/kpi/api";
import type { KpiPeriod } from "@/features/kpi/components/PeriodSelector";
import { cn } from "@/lib/utils";

import { useIsDark } from "../agCharts";
import { AgChartPanel } from "../components/AgChartPanel";
import { RechartsPanel } from "../components/RechartsPanel";
import {
  buildAgOptions,
  buildDatasets,
  CHART_TYPE_LABEL,
  type ChartType,
  type DatasetKey,
} from "../datasets";
import { SAMPLE_DASHBOARD } from "../sample";

const PERIODS: { value: KpiPeriod; label: string }[] = [
  { value: "MTD", label: "MTD" },
  { value: "QTD", label: "QTD" },
  { value: "YTD", label: "YTD" },
  { value: "LAST_QUARTER", label: "Last Quarter" },
];

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
        active
          ? "border-brand bg-brand text-white"
          : "border-border bg-card text-muted-foreground hover:border-brand hover:text-brand",
      )}
    >
      {children}
    </button>
  );
}

function PillRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="mr-1 w-20 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

export function ChartsLabPage() {
  const [period, setPeriod] = useState<KpiPeriod>("YTD");
  const [datasetKey, setDatasetKey] = useState<DatasetKey>("products");
  const [chartType, setChartType] = useState<ChartType>("line");

  const isDark = useIsDark();
  const dashboard = useKpiDashboard(period);
  const refresh = useKpiRefresh(period);

  // Prefer live Snowflake data; fall back to a clearly-labeled sample only when
  // the live feed errors (no backend / missing kpi.read) so the lab still demos.
  const usingSample = !dashboard.data && dashboard.isError;
  const source = dashboard.data ?? (usingSample ? SAMPLE_DASHBOARD : null);

  const datasets = useMemo(
    () => (source ? buildDatasets(source, isDark) : []),
    [source, isDark],
  );

  const dataset = datasets.find((d) => d.key === datasetKey) ?? datasets[0];
  const activeChartType: ChartType | undefined = dataset
    ? dataset.chartTypes.includes(chartType)
      ? chartType
      : dataset.chartTypes[0]
    : undefined;

  return (
    <div className="mx-auto flex max-w-[100rem] flex-col gap-4 pb-10">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <BarChart3 className="h-6 w-6 text-brand" />
            Charts Lab
            <Badge variant="secondary" className="ml-1 align-middle text-[10px]">
              evaluation
            </Badge>
          </h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Trying out{" "}
            <a
              href="https://www.ag-grid.com/charts/react/quick-start/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 font-medium text-brand hover:underline"
            >
              AG Charts <ExternalLink className="h-3 w-3" />
            </a>{" "}
            on live Snowflake KPI data. Each dataset is drawn with AG Charts and
            with recharts (what we use today) from the same numbers and brand
            palette — so we can compare rendering, interactions, and theming.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-brand hover:text-brand disabled:opacity-60"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
            Refresh from Snowflake
          </button>
          {source && (
            <span className="text-[11px] text-muted-foreground">
              {usingSample ? "Sample data" : "Live · Snowflake"} · refreshed{" "}
              {source.refreshed_at.slice(0, 10)}
            </span>
          )}
        </div>
      </header>

      {/* ── Controls ───────────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-3 p-4">
        <PillRow label="Period">
          {PERIODS.map((p) => (
            <Pill key={p.value} active={p.value === period} onClick={() => setPeriod(p.value)}>
              {p.label}
            </Pill>
          ))}
        </PillRow>
        <PillRow label="Dataset">
          {datasets.length === 0 ? (
            <span className="text-xs text-muted-foreground">
              {dashboard.isLoading ? "Loading…" : "No chartable data in this period."}
            </span>
          ) : (
            datasets.map((d) => (
              <Pill
                key={d.key}
                active={d.key === dataset?.key}
                onClick={() => setDatasetKey(d.key as DatasetKey)}
              >
                {d.label}
              </Pill>
            ))
          )}
        </PillRow>
        <PillRow label="Chart type">
          {dataset ? (
            dataset.chartTypes.map((t) => (
              <Pill key={t} active={t === activeChartType} onClick={() => setChartType(t)}>
                {CHART_TYPE_LABEL[t]}
              </Pill>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </PillRow>
        {dataset && (
          <p className="text-xs text-muted-foreground">{dataset.description}</p>
        )}
      </Card>

      {/* ── States ─────────────────────────────────────────────────────── */}
      {dashboard.isLoading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-[26rem] w-full" />
          <Skeleton className="h-[26rem] w-full" />
        </div>
      ) : dataset && activeChartType ? (
        <>
          {usingSample && (
            <Alert variant="info">
              <AlertTitle>Showing sample data</AlertTitle>
              <AlertDescription>
                The live KPI feed (<code>/api/kpi/dashboard</code>) isn’t reachable here, so the
                lab is plotting a labeled sample to demo the libraries. On Spark (backend +
                kpi.read) these panels show live Snowflake data.
              </AlertDescription>
            </Alert>
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title="AG Charts"
              badges={[
                { text: "v14 · community", tone: "brand" as const },
                { text: "MIT · free", tone: "success" as const },
              ]}
            >
              <AgChartPanel dataset={dataset} chartType={activeChartType} isDark={isDark} />
            </ChartCard>
            <ChartCard
              title="Recharts"
              badges={[{ text: "in use today · v2", tone: "muted" as const }]}
            >
              <RechartsPanel dataset={dataset} chartType={activeChartType} />
            </ChartCard>
          </div>

          <RawOptions
            json={JSON.stringify(
              {
                ...buildAgOptions(dataset, activeChartType, isDark),
                data: `⟨${dataset.rows.length} rows from Snowflake⟩`,
              },
              (_k, v) => (typeof v === "function" ? "(formatter fn)" : v),
              2,
            )}
          />
        </>
      ) : (
        <Alert variant="info">
          <AlertTitle>Nothing to plot</AlertTitle>
          <AlertDescription>
            This period returned no chartable series. Try another period.
          </AlertDescription>
        </Alert>
      )}

      <Findings />
    </div>
  );
}

// ─── presentational bits ────────────────────────────────────────────────────

function ChartCard({
  title,
  badges,
  children,
}: {
  title: string;
  badges: { text: string; tone: "brand" | "success" | "muted" }[];
  children: React.ReactNode;
}) {
  const toneClass: Record<string, string> = {
    brand: "bg-brand-light text-brand",
    success: "bg-success-light text-success",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-bold">{title}</h2>
        <div className="flex flex-wrap gap-1">
          {badges.map((b) => (
            <span
              key={b.text}
              className={cn(
                "rounded-md px-1.5 py-0.5 text-[10px] font-semibold",
                toneClass[b.tone],
              )}
            >
              {b.text}
            </span>
          ))}
        </div>
      </div>
      {children}
    </Card>
  );
}

function RawOptions({ json }: { json: string }) {
  return (
    <details className="rounded-lg border border-border bg-card">
      <summary className="cursor-pointer select-none px-4 py-2.5 text-xs font-semibold text-muted-foreground">
        AG Charts <code>options</code> for this chart (the API you'd write)
      </summary>
      <pre className="max-h-80 overflow-auto border-t border-border px-4 py-3 text-[11px] leading-relaxed text-muted-foreground">
        {json}
      </pre>
    </details>
  );
}

function Findings() {
  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-bold">What we learned — is AG Charts an option?</h2>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-success-light text-success">
              Community — free
            </Badge>
            <span className="text-[11px] text-muted-foreground">MIT · no license key</span>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            <li>
              Line, area, bar/column, scatter, bubble, pie, doughnut — plus
              legends, tooltips, animations and pan/zoom interactions.
            </li>
            <li>
              Covers <strong>every chart cinch-ops draws today</strong> (all
              recharts usage is area/line/bar).
            </li>
            <li>Built-in interactivity is richer out of the box than our hand-rolled recharts tooltips.</li>
          </ul>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-warning-light text-warning">
              Enterprise — $499/dev/yr
            </Badge>
            <span className="text-[11px] text-muted-foreground">only if we need these</span>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            <li>
              Candlestick/OHLC, heatmap, treemap, sunburst, waterfall,{" "}
              <strong>sankey</strong>, gauges, radar, maps, navigator, sync.
            </li>
            <li>
              The KPI <strong>funnel sankey</strong> is the one viz we have that
              would land on the paid tier (or stay custom).
            </li>
          </ul>
        </div>
      </div>
      <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
        <strong className="text-foreground">Theming takeaway:</strong> AG Charts
        paints to <code>&lt;canvas&gt;</code>, so brand colors and dark mode can’t
        come from CSS vars the way they do in recharts — they’re resolved in JS
        and the theme is rebuilt when the <code>.dark</code> class flips (toggle
        the app theme and watch both panels follow). recharts stays a bit simpler
        for pure DS-token theming; AG Charts trades that for stronger built-in
        interaction and a much wider chart catalog. Both are viable — the call is
        whether AG’s polish/coverage justifies adding (or switching to) a second,
        heavier charting dependency.
      </div>
    </Card>
  );
}
